import { JsonArray, JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { EnrichedScenario, ScenariosFilter } from './scenariosFilter.js';

/**
 * One AppSite-inventory ROW of the scenario table (87* tier). Carries the row's identity triple
 * (appSite, appView, requestJson) plus the per-AppSite inventory counts the server gathered.
 *
 * Deliberately a NEW model rather than extra fields on the shared EnrichedScenario: the scenario picker
 * (80/81/82…) has no use for these counts, so they stay off its hot path.
 */
export class ScenarioTableRow {
    // Identity: reuse EnrichedScenario so the shared folder/skip filter applies to a table row unchanged.
    scenario: EnrichedScenario = new EnrichedScenario();

    // The RequestJson config name (Scenario.configName). Empty for a template-derived scenario. Together
    // with scenario.appSite + scenario.appView it forms the unique row key (user decision 2026-08-31).
    requestJson: string = '';

    // Per-AppSite inventory counts, gathered SERVER-side (the pure filter never touches ConfigFile / IO).
    components: number = 0;      // component templates (files under a Component/ folder)
    globalData: number = 0;      // Global/globaldata*.json files
    globalViewData: number = 0;  // Global/globalviewdata*.json files
    dynData: number = 0;         // dynamicdata.json handler bindings
    dynAction: number = 0;       // dynamicaction.json handler bindings
    dynView: number = 0;         // dynamicview.json handler bindings
    dynClient: number = 0;       // dynamicclientdata.json handler bindings (ClientData consumer side)
    dynClientIface: number = 0;  // templates declaring a data-client-handler attribute (IClient side)
}

/**
 * The scenario TABLE's SHARED, PURE parse/filter/project logic — the single source of truth for the
 * AppSite-inventory table (87* tier), mirroring ScenariosFilter for the picker tier.
 *
 * Design:
 * DEPENDENCY-FREE, exactly like ScenariosFilter: no file IO, no ConfigFile, no registry. The per-AppSite
 * counts are gathered server-side and arrive already stamped on the raw island; this type only parses,
 * filters and projects. That purity is what lets the client tier run the byte-identical filter in the
 * browser, in --apiclient and in --ociclient (the single-source guarantee).
 */
export class ScenarioTableFilter {
    /**
     * Column definitions in render order: [short header shown in the <th>, full name shown in the legend].
     * The header row and the legend both derive from this ONE list so they can never drift, and the Cells
     * built in buildTableResult follow the same order.
     */
    private static readonly ColumnDefs: ReadonlyArray<[string, string]> = [
        ['AppSite', 'AppSite'],
        ['AppView', 'AppView'],
        ['ReqJson', 'RequestJson config name (empty = template scenario)'],
        ['Comp', 'Components — component templates in the AppSite'],
        ['GD', 'GlobalData — Global/globaldata*.json files'],
        ['GVD', 'GlobalViewData — Global/globalviewdata*.json files'],
        ['DD', 'DynData — dynamicdata.json handler bindings'],
        ['DA', 'DynAction — dynamicaction.json handler bindings'],
        ['DV', 'DynView — dynamicview.json handler bindings'],
        ['DC', 'DynClient consumer — dynamicclientdata.json handler bindings'],
        ['IC', 'IClient — templates declaring a data-client-handler attribute'],
    ];

    /**
     * Converts a raw scenario-table JsonArray (as produced by ScenarioTableData.getRawData) into a list of
     * ScenarioTableRow. Shared by the server processData and the client processor so both parse the raw
     * island identically.
     */
    static parseTableRows(rawScenariosArray: JsonArray | null): ScenarioTableRow[] {
        const rows: ScenarioTableRow[] = [];
        if (rawScenariosArray === null || rawScenariosArray === undefined) {
            return rows;
        }

        for (let i = 0; i < rawScenariosArray.length; i++) {
            const item = rawScenariosArray.get(i);
            if (!item.isObject) {
                continue;
            }
            const obj = item.getObject();
            const row = new ScenarioTableRow();
            row.scenario = new EnrichedScenario(
                ScenarioTableFilter._getStr(obj, 'appSite'),
                ScenarioTableFilter._getStr(obj, 'appView'),
                ScenarioTableFilter._getStr(obj, 'displayName'),
                ScenarioTableFilter._getStr(obj, 'description'),
                ScenarioTableFilter._getStr(obj, 'sourceFolder')
            );
            row.requestJson = ScenarioTableFilter._getStr(obj, 'requestJson');
            row.components = ScenarioTableFilter._getInt(obj, 'components');
            row.globalData = ScenarioTableFilter._getInt(obj, 'globalData');
            row.globalViewData = ScenarioTableFilter._getInt(obj, 'globalViewData');
            row.dynData = ScenarioTableFilter._getInt(obj, 'dynData');
            row.dynAction = ScenarioTableFilter._getInt(obj, 'dynAction');
            row.dynView = ScenarioTableFilter._getInt(obj, 'dynView');
            row.dynClient = ScenarioTableFilter._getInt(obj, 'dynClient');
            row.dynClientIface = ScenarioTableFilter._getInt(obj, 'dynClientIface');
            rows.push(row);
        }
        return rows;
    }

    /**
     * Serialises a list of ScenarioTableRow back into the raw scenario-table JsonArray — the exact inverse
     * of parseTableRows, emitting the same keys in the same order. This is the single owner of the raw-island
     * wire shape: the server raw source (ScenarioTableData) and the client-data preparer both build the island
     * through here, so the two tiers can never drift on key order or naming. Pure — no IO.
     */
    static serializeTableRows(rows: ScenarioTableRow[]): JsonArray {
        const scenariosArray = new JsonArray();
        if (rows === null || rows === undefined) {
            return scenariosArray;
        }

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const obj = new JsonObject();
            obj.set('appSite', row.scenario.appSite);
            obj.set('appView', row.scenario.appView);
            obj.set('requestJson', row.requestJson);
            obj.set('displayName', row.scenario.displayName);
            obj.set('description', row.scenario.description);
            obj.set('sourceFolder', row.scenario.sourceFolder);
            obj.set('components', row.components);
            obj.set('globalData', row.globalData);
            obj.set('globalViewData', row.globalViewData);
            obj.set('dynData', row.dynData);
            obj.set('dynAction', row.dynAction);
            obj.set('dynView', row.dynView);
            obj.set('dynClient', row.dynClient);
            obj.set('dynClientIface', row.dynClientIface);
            scenariosArray.add(obj);
        }
        return scenariosArray;
    }

    /**
     * Applies the scenario picker's pure folder/skip filter to the table rows, REUSING
     * ScenariosFilter.filterScenarios so the table and the picker apply a byte-identical filter. The reused
     * filter returns the same EnrichedScenario references it was given, so the surviving rows are recovered
     * by reference identity.
     */
    static filterTableRows(rows: ScenarioTableRow[], selectedFolder: string, skipViewFolders: string, skipViewAppSites: string): ScenarioTableRow[] {
        const enriched: EnrichedScenario[] = [];
        for (let i = 0; i < rows.length; i++) {
            enriched.push(rows[i].scenario);
        }

        const kept = ScenariosFilter.filterScenarios(enriched, selectedFolder, skipViewFolders, skipViewAppSites);

        const keptSet = new Set<EnrichedScenario>(kept); // reference identity
        const result: ScenarioTableRow[] = [];
        for (let i = 0; i < rows.length; i++) {
            if (keptSet.has(rows[i].scenario)) {
                result.push(rows[i]);
            }
        }
        return result;
    }

    /**
     * Returns the single page slice of rows for the given 1-based pageNo and itemsPerPage. A non-positive
     * itemsPerPage means "no paging" and returns the list unchanged. PageNo is clamped into [1 .. totalPages]
     * so a misconfigured page never renders an empty table for a non-empty result. PURE — no IO.
     */
    static paginateRows(rows: ScenarioTableRow[], itemsPerPage: number, pageNo: number): ScenarioTableRow[] {
        if (itemsPerPage <= 0 || rows.length === 0) {
            return rows;
        }

        const totalPages = Math.floor((rows.length + itemsPerPage - 1) / itemsPerPage);
        if (pageNo < 1) pageNo = 1;
        if (pageNo > totalPages) pageNo = totalPages;

        const startIndex = (pageNo - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;
        if (endIndex > rows.length) endIndex = rows.length;

        const page: ScenarioTableRow[] = [];
        for (let i = startIndex; i < endIndex; i++) {
            page.push(rows[i]);
        }
        return page;
    }

    /**
     * Projects the filtered rows into the engine's nested-table shape (Columns[] + Rows[].Cells[] + Legend[]).
     * Also carries ScenariosCount (the FULL filtered total, so it stays honest under paging) and the resolved
     * SelectedAppSiteFolder. When itemsPerPage is positive the Rows carry only the requested page (1-based
     * pageNo) and the result additionally carries paging metadata; a non-positive itemsPerPage renders every
     * filtered row (no paging).
     */
    static buildTableResult(filteredRows: ScenarioTableRow[], selectedFolder: string, itemsPerPage: number = 0, pageNo: number = 1): JsonObject {
        const retDataJson = new JsonObject();

        // Total BEFORE the page slice, so ScenariosCount and the paging metadata report the real count.
        const totalRows = filteredRows.length;
        const pageRows = ScenarioTableFilter.paginateRows(filteredRows, itemsPerPage, pageNo);

        // Header columns (short names) from the single column definition list.
        const columnsArray = new JsonArray();
        for (let i = 0; i < ScenarioTableFilter.ColumnDefs.length; i++) {
            const colObj = new JsonObject();
            colObj.set('Name', ScenarioTableFilter.ColumnDefs[i][0]);
            columnsArray.add(colObj);
        }
        retDataJson.set('Columns', columnsArray);

        // Body rows: one Cells array per row, values in the SAME order as ColumnDefs.
        const rowsArray = new JsonArray();
        for (let i = 0; i < pageRows.length; i++) {
            const row = pageRows[i];
            const cells = new JsonArray();
            ScenarioTableFilter._addCell(cells, row.scenario.appSite);
            ScenarioTableFilter._addCell(cells, row.scenario.appView === '' ? '-' : row.scenario.appView);
            ScenarioTableFilter._addCell(cells, row.requestJson === '' ? '-' : row.requestJson);
            ScenarioTableFilter._addCell(cells, row.components);
            ScenarioTableFilter._addCell(cells, row.globalData);
            ScenarioTableFilter._addCell(cells, row.globalViewData);
            ScenarioTableFilter._addCell(cells, row.dynData);
            ScenarioTableFilter._addCell(cells, row.dynAction);
            ScenarioTableFilter._addCell(cells, row.dynView);
            ScenarioTableFilter._addCell(cells, row.dynClient);
            ScenarioTableFilter._addCell(cells, row.dynClientIface);

            const rowObj = new JsonObject();
            rowObj.set('Index', i);
            rowObj.set('Cells', cells);
            rowsArray.add(rowObj);
        }
        retDataJson.set('Rows', rowsArray);
        retDataJson.set('RowCount', rowsArray.length);

        // Legend: only the abbreviated columns (short != full) need a key line.
        const legendArray = new JsonArray();
        for (let i = 0; i < ScenarioTableFilter.ColumnDefs.length; i++) {
            if (ScenarioTableFilter.ColumnDefs[i][0] !== ScenarioTableFilter.ColumnDefs[i][1]) {
                const legendObj = new JsonObject();
                legendObj.set('Short', ScenarioTableFilter.ColumnDefs[i][0]);
                legendObj.set('Full', ScenarioTableFilter.ColumnDefs[i][1]);
                legendArray.add(legendObj);
            }
        }
        retDataJson.set('Legend', legendArray);

        // ScenariosCount is the FULL filtered total (not the page slice), so it stays truthful under paging.
        retDataJson.set('ScenariosCount', totalRows);

        // Paging metadata — emitted only when paging is active, so the non-paged shape is unchanged.
        if (itemsPerPage > 0) {
            const totalPages = totalRows === 0 ? 0 : Math.floor((totalRows + itemsPerPage - 1) / itemsPerPage);
            const effectivePageNo = pageNo < 1 ? 1 : (totalPages > 0 && pageNo > totalPages ? totalPages : pageNo);

            retDataJson.set('ItemsPerPage', itemsPerPage);
            retDataJson.set('PageNo', effectivePageNo);
            retDataJson.set('TotalPages', totalPages);
            retDataJson.set('TotalRows', totalRows);

            retDataJson.set('HasPrev', effectivePageNo > 1);
            retDataJson.set('HasNext', totalPages > 0 && effectivePageNo < totalPages);
            retDataJson.set('FirstPageNo', 1);
            retDataJson.set('PrevPageNo', effectivePageNo > 1 ? effectivePageNo - 1 : 1);
            retDataJson.set('NextPageNo', effectivePageNo < totalPages ? effectivePageNo + 1 : (totalPages > 0 ? totalPages : 1));
            retDataJson.set('LastPageNo', totalPages > 0 ? totalPages : 1);

            retDataJson.set('PageInfo', `Page ${effectivePageNo} of ${totalPages} (${totalRows} rows)`);

            // Size-select options (87A) — one row per offered size (2 / 5 / 10) with the active size marked
            // Selected. Delivered as an ARRAY (not three booleans) so the template's {{?Selected}} sits INSIDE
            // {{@ItemsPerPageOptions}}: a marker inside an array is left in place by the decorator.
            const sizeOptionsArray = new JsonArray();
            const sizes = [2, 5, 10];
            for (let s = 0; s < sizes.length; s++) {
                const size = sizes[s];
                const sizeObj = new JsonObject();
                sizeObj.set('Value', size);
                sizeObj.set('Label', size);
                sizeObj.set('Selected', itemsPerPage === size);
                sizeOptionsArray.add(sizeObj);
            }
            retDataJson.set('ItemsPerPageOptions', sizeOptionsArray);

            // Numbered pages (87B) — one row per page, IsCurrent marks the active page.
            const pagesArray = new JsonArray();
            for (let p = 1; p <= totalPages; p++) {
                const pageObj = new JsonObject();
                pageObj.set('Num', p);
                pageObj.set('IsCurrent', p === effectivePageNo);
                pagesArray.add(pageObj);
            }
            retDataJson.set('Pages', pagesArray);
        }

        if (selectedFolder !== null && selectedFolder !== undefined && selectedFolder !== '') {
            retDataJson.set('SelectedAppSiteFolder', selectedFolder);
        }
        return retDataJson;
    }

    private static _addCell(cells: JsonArray, value: string | number): void {
        const cellObj = new JsonObject();
        cellObj.set('Value', value);
        cells.add(cellObj);
    }

    /**
     * Resolves an integer setting from the live refresh config first, then the param-file default, then
     * fallback. The refresh (configJson) is authoritative because it carries what the user just did (turned a
     * page, changed the size); paramJson only seeds the first render. Pure — shared by the server
     * ScenarioTableData and the client processor so both resolve paging identically.
     */
    static resolveIntSetting(configJson: JsonObject | null, paramJson: JsonObject | null, key: string, fallback: number): number {
        const fromConfig = ScenarioTableFilter._tryReadInt(configJson, key);
        if (fromConfig !== null) {
            return fromConfig;
        }
        const fromParam = ScenarioTableFilter._tryReadInt(paramJson, key);
        if (fromParam !== null) {
            return fromParam;
        }
        return fallback;
    }

    /**
     * Reads an integer from a JSON object, tolerating a JSON int or a JSON string (param files are
     * hand-authored and refresh values arrive over the wire, so either shape may appear). Returns null when
     * the key is absent or unparseable.
     */
    private static _tryReadInt(obj: JsonObject | null, key: string): number | null {
        if (obj === null || obj === undefined || !obj.containsKey(key)) {
            return null;
        }
        const val = obj.get(key);
        if (val.isInteger) {
            return val.getInteger();
        }
        const parsed = parseInt(val.toString(), 10);
        return isNaN(parsed) ? null : parsed;
    }

    private static _getStr(obj: JsonObject, key: string): string {
        return obj.containsKey(key) && obj.get(key).isString ? obj.get(key).getString() : '';
    }

    private static _getInt(obj: JsonObject, key: string): number {
        return obj.containsKey(key) && obj.get(key).isInteger ? obj.get(key).getInteger() : 0;
    }
}
